import "@fontsource/inter";
import "@fontsource/inter/500.css";
import "@fontsource/inter/700.css";
import "@fontsource/dm-serif-text";
import codeTheme from "@theme-ui/prism/presets/night-owl.json";
import { Theme, ThemeUIContextValue, useThemeUI } from "theme-ui";
import { buttons } from "./buttons";
import { cards } from "./cards";
import { colors, defaultPalette } from "./colors";
import { components } from "./components";
import { forms } from "./forms";
import { navigation } from "./navigation";
import { text } from "./text";
import { badges } from "./badges";
import { darken } from "@theme-ui/color";

export const theme: Theme = {
  breakpoints: ["901px", "1025px", "1281px", "1581px", "1600"],
  borderWidths: [0, 1, 4],
  colors: colors({ palette: defaultPalette }),
  fonts: {
    brand: "DM Serif Text, serif",
    body: "Inter, Inconsolata, monospace",
    heading: "Inter, Inconsolata, monospace",
    code: "monospace"
  },
  fontWeights: {
    reg: 400,
    med: 500,
    semi: 600,
    heading: 700,
    bold: 700
  },
  lineHeights: {
    body: 1.5,
    heading: 1.125
  },
  fontSizes: [10, 12, 14, 16, 20, 24, 32, 48, 56, 88],
  space: [0, 4, 8, 12, 16, 24, 32, 48, 64],
  shadows: [
    "-9px 95px 38px rgba(50, 46, 1, 0.01), -5px 54px 32px rgba(50, 46, 1, 0.05), -2px 24px 24px rgba(50, 46, 1, 0.09), -1px 6px 13px rgba(50, 46, 1, 0.1), 0px 0px 0px rgba(50, 46, 1, 0.1);",
    "0px 2px 8px rgba(90, 65, 2, 0.12)",
    "0px 4px 8px rgba(0, 0, 0, 0.35)",
    "0px 3px 8px 0px rgba(10, 6, 60, 0.06), 0px 14px 14px 0px rgba(10, 6, 60, 0.05), 0px 31px 19px 0px rgba(10, 6, 60, 0.03), 0px 56px 22px 0px rgba(10, 6, 60, 0.01), 0px 87px 24px 0px rgba(10, 6, 60, 0.00)",
    "0px 4px 12px rgba(0, 0, 0, 0.08)",
    "0px 8px 24px -10px rgba(0, 0, 0, 0.16)",
    "0px 115px 46px rgba(18, 16, 152, 0.01), 0px 65px 39px rgba(18, 16, 152, 0.02), 0px 29px 29px rgba(18, 16, 152, 0.03), 0px 7px 16px rgba(18, 16, 152, 0.03), 0px 0px 0px rgba(18, 16, 152, 0.03)",
    "0px 1px 0px rgba(0, 0, 0, 0.08)",
    "0px 37px 15px rgba(45, 5, 72, 0.01), 0px 21px 13px rgba(45, 5, 72, 0.02), 0px 9px 9px rgba(45, 5, 72, 0.03), 0px 2px 5px rgba(45, 5, 72, 0.04), 0px 0px 0px rgba(45, 5, 72, 0.04)",
    "0px 8px 21px -3px rgba(24, 24, 119, 0.16), 0px 0px 0px 2px rgba(92, 91, 238, 0.21)",
    "0px 12px 48px rgba(0, 0, 0, 0.32)",
    "0px 8px 24px rgba(0, 0, 0, 0.12)"
  ],
  radii: [8, 12],
  styles: {
    root: {
      fontFamily: "body",
      fontWeight: "reg",
      fontSize: 1,
      lineHeight: "body",
      color: "black100",
      a: {
        variant: "styles.focus"
      },
      pre: {
        ...codeTheme,
        fontFamily: "body",
        borderRadius: 0,
        overflow: "auto",
        fontSize: "16px",
        p: 3,
        my: "48px!important"
      }
    },
    focus: {
      transition: ".2s linear box-shadow",
      ":focus": {
        outline: "none"
      }
    },
    heroJumbo: {
      fontSize: 9
    },
    heroHuge: {
      fontSize: 8
    },
    headerLarge: {
      fontSize: 7
    },
    headerMedium: {
      fontWeight: "heading",
      letterSpacing: "-0.02em",
      fontSize: 6
    },
    headerSmall: {
      fontWeight: "med",
      fontSize: 5
    },
    headerTiny: {
      fontWeight: "med",
      fontSize: 4
    },
    titleLarge: {
      fontWeight: "heading",
      fontSize: 3
    },
    titleMedium: {
      fontWeight: "heading",
      fontSize: 2
    },
    titleSmall: {
      fontWeight: "heading",
      fontSize: 1
    },
    bodyLarge: {
      fontSize: 3,
      fontWeight: "reg"
    },
    bodyMedium: {
      fontSize: 2,
      fontWeight: "reg"
    },
    bodySmall: {
      fontSize: 1,
      fontWeight: "reg"
    },
    label: {
      fontSize: 2,
      letterSpacing: "0.03em",
      textTransform: "uppercase",
      fontWeight: "bold"
    },
    labelSmall: {
      fontSize: 1,
      letterSpacing: "0.03em",
      textTransform: "uppercase",
      fontWeight: "bold"
    },
    p: {
      mt: 0,
      mb: 3,
      code: {
        variant: "styles.code"
      }
    },
    small: {
      color: "muted",
      fontSize: 0
    },
    a: {
      color: "muted",
      variant: "styles.focus"
    },

    hr: {
      borderBottomWidth: "thin",
      borderBottomStyle: "solid",
      borderBottomColor: "borders.muted",
      mt: 1,
      mb: 0
    },
    divider: {
      borderBottomWidth: "2px",
      borderBottomStyle: "solid",
      borderBottomColor: "borders.dark"
    },
    spinner: {
      primary: {
        color: "text.default"
      },
      secondary: {
        color: "text.default"
      },
      tertiary: {
        color: "text.highlight"
      },
      success: {
        color: "success"
      },
      error: {
        color: "error"
      }
    },
    components
  },

  buttons,
  cards,
  links: {
    ...navigation,
    inline: {
      color: "text.highlight",
      textDecoration: "underline",
      cursor: "pointer",
      ":hover": {
        color: darken("text.highlight", 0.1)
      }
    }
  },

  text,

  forms,
  badges
};

export const useTheme = useThemeUI as unknown as () => ThemeUIContextValue;
